import { Layout, Spin } from 'antd';
import {
  ActiveShiftsModal,
  Header,
  Loadable,
  OmniChannelFloatingIncomingCall,
  SecondSiderMenu,
  SidebarMenu
} from 'components';
import FailedLoginWorldPhoneWarningModal from 'components/customer-lookup/FailedLoginWorldPhoneWarningModal';
import { OmniCallProvider } from 'contexts';
import dayjs from 'dayjs';
import { useGetShiftMeActive, useGetSoftPhoneToken, useLoginSoftPhone, useProfile } from 'hooks';
import { shiftsMessages } from 'messages';
import { customerLookupMessages } from 'messages/customer-lookup.message';
import ErrorBoundary from 'pages/500';
import { LegacyRef, useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ROUTE } from 'routes/constants';
import {
  connectOmniChannelSocket,
  disconnectOmniChannelSocket,
  onReceiveCallCdrEvent,
  onReceiveCallDialingEvent,
  onReceiveCallHangUpEvent
} from 'services';
import { twMerge } from 'tailwind-merge';
import { LOCAL_STORAGE_KEY, PERMISSION } from 'utils';
import { checkPermissions } from 'utils/check-permission';

const { Sider } = Layout;

function MainLayout() {
  const { isAuthenticated, profile } = useProfile();
  const { isLoading: isLoadingShiftMeActive, hasSelectShiftPermission } = useGetShiftMeActive();
  const [collapsedSidebar, setCollapsedSidebar] = useState(false);
  const { onGetWorldPhoneToken } = useGetSoftPhoneToken();
  const { onLoginSoftPhoneHandle } = useLoginSoftPhone();
  const [showPopUpReceiveCall, setShowPopUpReceiveCall] = useState(false);
  const [customerNumber, setCustomerNumber] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [openFailedLoginWorldPhoneWarning, setOpenFailedLoginWorldPhoneWarning] = useState(false);
  const currentPath = location.pathname;
  const layoutRef = useRef<HTMLDivElement | null>(null);

  const { userPermissionIds } = useProfile();
  const isAllowCallCustomer = checkPermissions(userPermissionIds, PERMISSION.CALL_CUSTOMER);

  const fetchAndLoginSoftPhone = async () => {
    if (!isAuthenticated || !profile?.extensionId) return;

    try {
      const res = await onGetWorldPhoneToken({ userExtension: profile.extensionId });
      if (res?.data?.token) {
        await onLoginSoftPhoneHandle({
          username: profile.username,
          token: res.data.token
        });
      }
    } catch (error) {
      setOpenFailedLoginWorldPhoneWarning(true);
      console.error('Error fetching or logging into softphone:', error);
    }
  };

  useEffect(() => {
    if (layoutRef.current) {
      layoutRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isAllowCallCustomer) {
      connectOmniChannelSocket();
      return () => {
        // disconnectOmniChannelSocket();
      };
    }
  }, []);

  useEffect(() => {
    if (isAllowCallCustomer && !profile?.extensionId) {
      setOpenFailedLoginWorldPhoneWarning(true);
    } else if (isAllowCallCustomer && profile?.extensionId) {
      const omniGrouId = profile?.omniGroupId;
      if (omniGrouId !== '2') {
        fetchAndLoginSoftPhone();
      }
    }
  }, [isAuthenticated, profile?.extensionId, userPermissionIds]);

  const navigateToCustomerLookUp = (): void => {
    navigate(`${ROUTE.CUSTOMER_LOOKUP}`);
    setCustomerNumber('');
    setShowPopUpReceiveCall(false);
  };

  useEffect(() => {
    // onReceiveCallDialAnswerEvent((data) => {
    //   if (data?.callUuid && data?.customerNumber) {
    //     setShowPopUpReceiveCall(true)
    //     setCustomerNumber(data?.customerNumber)
    //   }
    // });

    onReceiveCallDialingEvent((data: any) => {
      if (data?.callUuid && data?.customerNumber) {
        // Determine if the current path is the customer lookup page.
        // ROUTE.CUSTOMER_LOOKUP should be specific (e.g., 'customer-lookup')
        // and not a prefix of other routes like '/profile' (e.g., not '/' or '/p').
        const isCustomerLookup = location.pathname.startsWith(ROUTE.CUSTOMER_LOOKUP);

        // This log helps debug the value of isCustomerLookup.
        // If on '/profile' and this logs 'true', then ROUTE.CUSTOMER_LOOKUP is matching '/profile'.

        // The popup is shown ONLY IF the current page is NOT the customer lookup page.
        // If isCustomerLookup is true when on '/profile', this condition (!isCustomerLookup)
        // will be false, and setShowPopUpReceiveCall(true) will not be executed.
        if (!isCustomerLookup) {
          setShowPopUpReceiveCall(true);
        }
        setCustomerNumber(data?.customerNumber);
      }
    });
    // onReceiveCallHangUpEvent((data: any) => {
    //   if (data?.callUuid) {
    //     setShowPopUpReceiveCall(false);
    //     setCustomerNumber('');
    //   }
    // });
    onReceiveCallCdrEvent((data) => {
      if (data?.callUuid) {
        setShowPopUpReceiveCall(false);
        setCustomerNumber('');
      }
    });
  }, []);
  useEffect(() => {
    if (currentPath === `/${ROUTE.CUSTOMER_LOOKUP}` && showPopUpReceiveCall) {
      setShowPopUpReceiveCall(false);
    }
  }, [currentPath]);

  if (!localStorage.getItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN) || !isAuthenticated) {
    return <Navigate to={ROUTE.LOGIN} replace />;
  }

  return (
    <OmniCallProvider>
      <Spin tip={shiftsMessages.loadShift} spinning={isLoadingShiftMeActive && hasSelectShiftPermission}>
        <Layout>
          <div onClick={() => navigateToCustomerLookUp()}>
            {showPopUpReceiveCall ? <OmniChannelFloatingIncomingCall customerNumber={customerNumber} r /> : null}
          </div>
          <Sider className='sider-layout'>
            <div className='relative'>
              <div className={twMerge('absolute left-0', collapsedSidebar ? 'z-[100]' : 'z-[2000]')}>
                <SidebarMenu onChangeCollapse={setCollapsedSidebar} />
              </div>
              <SecondSiderMenu />
            </div>
          </Sider>
          <Layout>
            <Header />
            <Layout ref={layoutRef} className='h-[calc(100vh-80px)] max-h-[calc(100vh-80px)] overflow-y-auto px-4 pb-5'>
              <ErrorBoundary>
                <Loadable>
                  <Outlet />
                </Loadable>
              </ErrorBoundary>
            </Layout>
          </Layout>
          <ActiveShiftsModal />
        </Layout>
      </Spin>
      {isAllowCallCustomer && !profile?.extensionId ? (
        <FailedLoginWorldPhoneWarningModal
          open={openFailedLoginWorldPhoneWarning}
          onCancel={() => {
            setOpenFailedLoginWorldPhoneWarning(false);
          }}
          content={customerLookupMessages.noExtensionFound}
        />
      ) : isAllowCallCustomer && profile?.extensionId ? (
        <FailedLoginWorldPhoneWarningModal
          open={openFailedLoginWorldPhoneWarning}
          onCancel={() => {
            setOpenFailedLoginWorldPhoneWarning(false);
          }}
          content={customerLookupMessages.openWorldPhoneWarning}
        />
      ) : null}
    </OmniCallProvider>
  );
}

export default MainLayout;
